import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

interface Ctx {
  unread: number;
  refresh: () => void;
  markAllRead: () => Promise<void>;
}

const NotifCtx = createContext<Ctx>({ unread: 0, refresh: () => {}, markAllRead: async () => {} });

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  const refresh = async () => {
    if (!user) { setUnread(0); return; }
    const { count } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("read", false);
    setUnread(count ?? 0);
  };

  const markAllRead = async () => {
    if (!user) return;
    await supabase.from("notifications").update({ read: true }).eq("user_id", user.id).eq("read", false);
    setUnread(0);
  };

  useEffect(() => {
    refresh();
    if (!user) return;
    const ch = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => refresh()
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user?.id]);

  useEffect(() => {
    if (typeof navigator !== "undefined" && "setAppBadge" in navigator) {
      try {
        if (unread > 0) {
          navigator.setAppBadge(unread);
        } else {
          navigator.clearAppBadge();
        }
      } catch {
        // Silencio: el navegador puede soportar la API pero fallar
        // (ej. app no instalada como PWA, sin permiso, etc.)
      }
    }
  }, [unread]);

  return <NotifCtx.Provider value={{ unread, refresh, markAllRead }}>{children}</NotifCtx.Provider>;
}

export const useNotifications = () => useContext(NotifCtx);
